import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'pages/AuthProvider';
import { useUserInfo } from 'pages/UserProvider';
import { getDefaultLandingPage } from 'routes/routes';
import { DIQAdminRoleName, DIQSupportRoleName } from '@/utils/userRoles';

export const companyIdParam = 'companyId';

const SPOOF_ROLES: readonly string[] = [DIQAdminRoleName, DIQSupportRoleName];

// setCompany swallows a failed spoof-token call (no state change, no error), so a
// switch can silently never complete. Give up after this long rather than holding
// the caller's render forever.
const SPOOF_TIMEOUT_MS = 10_000;

/**
 * Applies a company context requested via the ?companyId= query param —
 * used by customer-success Slack deep links (ENG-573). A Slack link opens a
 * fresh tab whose sessionStorage carries no spoof state, so a member-profile
 * link for another company would 404; this hook spoofs into the requested
 * company first, then strips the param.
 *
 * Returns true while the switch is pending so the caller can hold rendering —
 * otherwise the target page's queries run against the wrong company and its
 * 404 redirect fires before the spoof completes.
 *
 * Non-internal roles can't spoof (enforced server-side too), so for them the
 * param is stripped without a company switch.
 *
 * If the spoof fails (company lookup rejects, or the switch never completes),
 * the user is sent to the default landing page — the deep-linked page would
 * only 404 against the wrong company.
 */
const useSpoofFromUrl = (): boolean => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, company, setCompany } = useUserInfo();
  const {
    clients: { company: companyClient },
  } = useAuth();
  const requestedCompanyId = searchParams.get(companyIdParam);
  const currentCompanyId = company?.id;
  const isPending = requestedCompanyId !== null && requestedCompanyId !== currentCompanyId;

  const clearParam = useCallback((): void => {
    setSearchParams(
      (params) => {
        params.delete(companyIdParam);
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const failSpoof = useCallback((): void => {
    navigate(getDefaultLandingPage(), { replace: true });
  }, [navigate]);

  // Bail-out: if the switch hasn't completed after SPOOF_TIMEOUT_MS, give up
  // and land on the default page instead of an indefinite spinner.
  useEffect(() => {
    if (!isPending) {
      return;
    }
    const timer = setTimeout(failSpoof, SPOOF_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isPending, failSpoof]);

  useEffect(() => {
    if (!requestedCompanyId || !user) {
      return;
    }

    // Already in the requested company (also reached after a successful
    // switch, which re-runs this effect with the updated company).
    if (requestedCompanyId === currentCompanyId) {
      clearParam();
      return;
    }

    if (!SPOOF_ROLES.includes(user.roleName ?? '')) {
      clearParam();
      return;
    }

    let cancelled = false;
    companyClient
      .getCompany({ id: requestedCompanyId })
      .then((companyResponse) => {
        if (!cancelled) {
          setCompany(companyResponse);
        }
      })
      .catch(() => {
        // Unknown company or no access.
        if (!cancelled) {
          failSpoof();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [requestedCompanyId, currentCompanyId, user, companyClient, setCompany, clearParam, failSpoof]);

  return isPending;
};

export default useSpoofFromUrl;
